import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Archive } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { PermissionsContent } from './permissions-page'
import { SummaryCardContent } from '@/features/summary-card/routes/summary-card-page'
import { SearchableFieldsContent } from '@/features/searchable-fields/routes/searchable-fields-page'
import { PublicLinksContent } from '@/features/public-links/routes/public-links-page'
import { NotificationPrefsContent } from '@/features/notifications/components/notification-prefs'

async function fetchProject(projectId: string) {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('id', projectId)
    .single()
  if (error) throw error
  return data
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'فعال' },
  { value: 'paused', label: 'متوقف' },
  { value: 'closed', label: 'بسته' },
]

export function SettingsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [department, setDepartment] = useState('')
  const [description, setDescription] = useState('')
  const [principalInvestigator, setPrincipalInvestigator] = useState('')
  const [institution, setInstitution] = useState('')
  const [irbReference, setIrbReference] = useState('')
  const [dataRetentionPolicy, setDataRetentionPolicy] = useState('')
  const [defaultCalendar, setDefaultCalendar] = useState('jalali')
  const [timeZone, setTimeZone] = useState('Asia/Tehran')
  const [statusConfirm, setStatusConfirm] = useState(false)
  const [newStatus, setNewStatus] = useState<string>('active')
  const [archiveConfirm, setArchiveConfirm] = useState(false)
  const [archiveConfirmText, setArchiveConfirmText] = useState('')

  const { data: project, isLoading } = useQuery({
    queryKey: ['project-settings', projectId],
    queryFn: () => fetchProject(projectId!),
    enabled: !!projectId,
  })

  useEffect(() => {
    if (!project) return
    setName(project.name ?? '')
    setCode(project.code ?? '')
    setDepartment(project.department ?? '')
    setDescription(project.description ?? '')
    setPrincipalInvestigator(project.principal_investigator ?? '')
    setInstitution(project.institution ?? '')
    setIrbReference(project.irb_reference ?? '')
    setDataRetentionPolicy(project.data_retention_policy ?? '')
    setDefaultCalendar(project.default_calendar ?? 'jalali')
    setTimeZone(project.time_zone ?? 'Asia/Tehran')
    setNewStatus(project.status)
  }, [project])

  const saveMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('projects')
        .update({
          name, code, department, description,
          principal_investigator: principalInvestigator,
          institution,
          irb_reference: irbReference,
          updated_by: user?.id,
        })
        .eq('id', projectId!)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-meta', projectId] })
      qc.invalidateQueries({ queryKey: ['project-settings', projectId] })
    },
  })

  const saveLocaleMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('projects')
        .update({ default_calendar: defaultCalendar, time_zone: timeZone, updated_by: user?.id })
        .eq('id', projectId!)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-settings', projectId] })
    },
  })

  const saveRetentionMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('projects')
        .update({ data_retention_policy: dataRetentionPolicy, updated_by: user?.id })
        .eq('id', projectId!)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-settings', projectId] })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('projects')
        .update({ status: 'archived', updated_by: user?.id })
        .eq('id', projectId!)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-meta', projectId] })
      qc.invalidateQueries({ queryKey: ['project-settings', projectId] })
      setArchiveConfirm(false)
      setArchiveConfirmText('')
    },
  })

  const statusMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId!)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-meta', projectId] })
      qc.invalidateQueries({ queryKey: ['project-settings', projectId] })
      setStatusConfirm(false)
    },
  })

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 bg-muted rounded animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="تنظیمات پروژه" breadcrumbs={[{ label: 'تنظیمات' }]} />

      <div className="flex-1 overflow-y-auto flex flex-col">
        <Tabs defaultValue="general" className="flex flex-col flex-1" dir="rtl">
          {/* Tab bar — full width, scrollable, not constrained by max-w-2xl */}
          <div className="overflow-x-auto border-b border-border shrink-0 px-6">
            <TabsList className="w-max h-auto rounded-none bg-transparent border-0 gap-0 px-0">
              {[
                { value: 'general', label: 'اطلاعات پروژه' },
                { value: 'locale', label: 'تنظیمات منطقه' },
                { value: 'status', label: 'وضعیت' },
                { value: 'retention', label: 'نگهداری داده' },
                { value: 'summary', label: 'کارت خلاصه' },
                { value: 'searchable', label: 'فیلدهای جستجو' },
                { value: 'permissions', label: 'دسترسی‌ها' },
                { value: 'notifications', label: 'اعلان‌ها' },
                { value: 'public-links', label: 'لینک‌های عمومی' },
              ].map(({ value, label }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-xs px-4 py-2.5 whitespace-nowrap"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Content — max-w-2xl only on content, not the tab bar */}
          <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl">

          <TabsContent value="general" className="space-y-5" dir="rtl">
            <div className="space-y-2">
              <Label>نام پروژه</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>کد پروژه</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} dir="ltr" />
              </div>
              <div className="space-y-2">
                <Label>دپارتمان</Label>
                <Input value={department} onChange={(e) => setDepartment(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>محقق اصلی (PI)</Label>
              <Input value={principalInvestigator} onChange={(e) => setPrincipalInvestigator(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>موسسه</Label>
                <Input value={institution} onChange={(e) => setInstitution(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>شماره مجوز اخلاق (IRB)</Label>
                <Input value={irbReference} onChange={(e) => setIrbReference(e.target.value)} dir="ltr" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>توضیحات</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="توضیح مختصر پروژه..."
              />
            </div>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="gap-1.5"
            >
              <Save className="w-4 h-4" />
              {saveMutation.isPending ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
            </Button>
          </TabsContent>

          <TabsContent value="locale" className="space-y-5" dir="rtl">
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div className="space-y-2">
                <Label>زبان پیش‌فرض</Label>
                <Input value="فارسی" disabled />
                <p className="text-xs text-muted-foreground">در نسخه ۱ فقط فارسی پشتیبانی می‌شود</p>
              </div>
              <div className="space-y-2">
                <Label>تقویم پیش‌فرض</Label>
                <Select value={defaultCalendar} onValueChange={setDefaultCalendar}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jalali">شمسی (جلالی)</SelectItem>
                    <SelectItem value="gregorian">میلادی</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>منطقه زمانی</Label>
                <Select value={timeZone} onValueChange={setTimeZone}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Asia/Tehran">تهران (UTC+3:30)</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="Asia/Dubai">دبی (UTC+4)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={() => saveLocaleMutation.mutate()}
              disabled={saveLocaleMutation.isPending}
              className="gap-1.5"
            >
              <Save className="w-4 h-4" />
              {saveLocaleMutation.isPending ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
            </Button>
          </TabsContent>

          <TabsContent value="retention" className="space-y-5" dir="rtl">
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground mb-1">سیاست نگهداری داده</p>
                <p className="text-sm text-muted-foreground">
                  توضیح سیاست نگهداری داده این پروژه را وارد کنید. این اطلاعات صرفاً جنبه اطلاعاتی دارد.
                </p>
              </div>
              <Textarea
                value={dataRetentionPolicy}
                onChange={(e) => setDataRetentionPolicy(e.target.value)}
                rows={5}
                placeholder="توضیح سیاست نگهداری داده..."
              />
            </div>
            <Button
              onClick={() => saveRetentionMutation.mutate()}
              disabled={saveRetentionMutation.isPending}
              className="gap-1.5"
            >
              <Save className="w-4 h-4" />
              {saveRetentionMutation.isPending ? 'در حال ذخیره...' : 'ذخیره'}
            </Button>

            {/* Archive */}
            <div className="mt-8 pt-6 border-t border-border">
              <h3 className="text-sm font-semibold text-foreground mb-2">بایگانی پروژه</h3>
              <p className="text-sm text-muted-foreground mb-4">
                پروژه بایگانی می‌شود و از دید پیش‌فرض پنهان خواهد شد. داده‌ها حفظ می‌شوند.
              </p>
              {project?.status !== 'archived' ? (
                <Button
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10"
                  onClick={() => setArchiveConfirm(true)}
                >
                  <Archive className="w-4 h-4 me-1.5" />
                  بایگانی پروژه
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">این پروژه در حال حاضر بایگانی شده است.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="summary" className="space-y-4" dir="rtl">
            <SummaryCardContent />
          </TabsContent>

          <TabsContent value="searchable" className="space-y-4" dir="rtl">
            <SearchableFieldsContent />
          </TabsContent>

          <TabsContent value="permissions" className="space-y-4" dir="rtl">
            <PermissionsContent />
          </TabsContent>

          <TabsContent value="notifications" className="space-y-4" dir="rtl">
            {projectId && <NotificationPrefsContent projectId={projectId} />}
          </TabsContent>

          <TabsContent value="public-links" className="space-y-4" dir="rtl">
            <PublicLinksContent />
          </TabsContent>

          <TabsContent value="status" className="space-y-5" dir="rtl">
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground mb-1">وضعیت فعلی پروژه</p>
                <p className="text-sm text-muted-foreground">
                  وضعیت پروژه نحوه دسترسی اعضا و ثبت سوابق را تعیین می‌کند.
                </p>
              </div>
              <div className="space-y-2">
                <Label>وضعیت جدید</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant={newStatus === project?.status ? 'outline' : 'default'}
                disabled={newStatus === project?.status}
                onClick={() => setStatusConfirm(true)}
              >
                تغییر وضعیت
              </Button>
            </div>
          </TabsContent>
          </div>{/* end max-w-2xl */}
          </div>{/* end inner scroll div */}
        </Tabs>
      </div>

      {/* Archive confirm */}
      <Dialog open={archiveConfirm} onOpenChange={setArchiveConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>بایگانی پروژه؟</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            برای تأیید، نام پروژه «<span className="text-foreground font-medium">{project?.name}</span>» را تایپ کنید.
          </p>
          <Input
            value={archiveConfirmText}
            onChange={(e) => setArchiveConfirmText(e.target.value)}
            placeholder={project?.name}
            className="mt-2"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setArchiveConfirm(false); setArchiveConfirmText('') }}>انصراف</Button>
            <Button
              variant="destructive"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveConfirmText !== project?.name || archiveMutation.isPending}
            >
              {archiveMutation.isPending ? 'در حال بایگانی...' : 'بایگانی'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusConfirm} onOpenChange={setStatusConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>تغییر وضعیت پروژه؟</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            وضعیت پروژه به «{STATUS_OPTIONS.find((o) => o.value === newStatus)?.label}» تغییر می‌کند.
            این تغییر فوری اعمال می‌شود.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusConfirm(false)}>انصراف</Button>
            <Button
              onClick={() => statusMutation.mutate()}
              disabled={statusMutation.isPending}
            >
              تأیید تغییر
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
